"use client";

import Container from "@/components-shared/container";
import ReservationDuration from "./components/reservation-duration";
import AdditionalDetails from "./components/AdditionalDetails";
import Description from "./components/Description";
import UnitDetails from "./components/UnitDetails";
import Host from "./components/Host";
import Feedback from "./components/Feedback";
import CarouselWithThumbs from "./components/carousel-with-thumbs";
import useSizes from "@/lib/window-sizes-hook";
import { use, useMemo, memo } from "react";
import pattern from "@/public/images/check-out-bg.svg";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerTitle,
  DrawerTrigger,
} from "@/components-shared/ui/drawer";
import { buttonVariants } from "@/components-shared/ui/button";
import TrustInfo from "./components/TrustInfo";
import Image from "next/image";
import { useLocale, useTranslations } from "next-intl";
import { useGetPropById } from "@/lib/get-prop-by-id";
import { useAppSelector } from "@/redux/store";
import { X } from "lucide-react";
import VrTour from "./components/VrTour";
import LoadingPage from "../../loading";

const UnitPage = (props: {
  params: Promise<{ unit: string }>;
  searchParams: Promise<{
    bookingId: string;
    checkInDate: Date;
    checkOutDate: Date;
  }>;
}) => {
  const { maxLg } = useSizes();
  const isClient = useAppSelector((state) => state.isBrowser);
  const searchParams = use(props.searchParams);
  const params = use(props.params);
  const locale = useLocale();
  const t = useTranslations("unitPage");

  const unitId = useMemo(() => params.unit, [params.unit]);
  const bookingId = useMemo(
    () => searchParams.bookingId,
    [searchParams.bookingId]
  );
  const checkInDate = useMemo(
    () => searchParams.checkInDate,
    [searchParams.checkInDate]
  );
  const checkOutDate = useMemo(
    () => searchParams.checkOutDate,
    [searchParams.checkOutDate]
  );
  const { data, isLoading } = useGetPropById(unitId);

  return (
    <div className="relative">
      {isLoading && (
        <div className="fixed z-50 inset-0 h-screen">
          <LoadingPage />
        </div>
      )}
      <Container className="relative pt-8">
        <CarouselWithThumbs data={data} />
        <div className="lg:grid grid-cols-5 gap-8 mt-12">
          <main className="col-span-3 flex flex-col gap-10 ">
            <VrTour view360url={data?.view360Url} />
            <TrustInfo />
            <UnitDetails
              code={data?.code}
              area={data?.area}
              lat={data?.location?.lat}
              lng={data?.location?.lng}
              numOfBathrooms={data?.numOfBathrooms}
              numOfBedrooms={data?.numOfBedrooms}
              numOfLivingRooms={
                data?.numOfAdditionalLivingrooms! +
                data?.numOfInternalLivingrooms! +
                data?.numOfExternalLivingrooms!
              }
              entertainment={data?.entertainment}
            />
            <Description
              description={
                locale === "ar"
                  ? data?.arabicDescription ?? data?.description
                  : data?.description ?? data?.arabicDescription
              }
            />
            <Host
              hostname={data?.owner?.firstName ?? "-------"}
              createdAt={data?.owner?.createdAt!}
              propsNum={data?.owner?._count?.properties || 0}
              ownerId={data?.owner?.id}
            />
            <Feedback
              hostname={data?.owner?.firstName}
              cleanliness={data?.cleanliness!}
              comfort={data?.comfort!}
              information={data?.information!}
              overallRating={data?.overallRating!}
              staff={data?.staff!}
            />
            <AdditionalDetails
              carParkingAvailable={data?.carParkingAvailable || false}
              createdAt={data?.createdAt}
              cancellationPolicy={data?.cancellationPolicy}
              insurance={data?.refundableAmount}
              refundableInsurance={data?.refundableInsurance || false}
              petsAllowed={data?.petsAllowed}
              checkInTime={data?.checkInTime}
              checkOutTime={data?.checkOutTime}
              isLoading={isLoading}
            />
          </main>

          {!maxLg ? (
            <aside className="col-span-2 ms-auto">
              <ReservationDuration
                unitName={data?.title}
                unitCode={data?.id}
                total={data?.totalPrice ?? ""}
                original={data?.originalPrice ?? ""}
                from={checkInDate}
                to={checkOutDate}
                bookingId={bookingId}
                insurance={data?.refundableAmount ?? ""}
                refundableInsurance={data?.refundableInsurance}
              />
            </aside>
          ) : null}
        </div>
      </Container>
      {maxLg && isClient ? (
        <nav className="bg-white w-full">
          <Drawer>
            <DrawerTrigger className="btm-nav border-t border-black !h-20 z-10 px-6 flex justify-between items-center">
              <p className="flex items-center gap-1">
                {data
                  ? data!.totalPrice < data!.originalPrice && (
                      <del className="font-light text-gray-light">
                        {Number(data?.originalPrice).toLocaleString("en-US")}
                      </del>
                    )
                  : ""}
                <span className="flex gap-1 items-center">
                  {data ? (
                    <strong className="text-green-main text-xl">
                      {Number(data?.totalPrice).toLocaleString("en-US")}
                    </strong>
                  ) : (
                    ""
                  )}
                  <span>{t("pricePerNight")}</span>
                </span>
              </p>
              <span
                className={buttonVariants({
                  size: "default",
                  className: "!text-base !leading-none !py-0",
                })}
              >
                {searchParams.checkInDate && searchParams.checkOutDate
                  ? t("bookNow")
                  : t("selectDuration")}
              </span>
            </DrawerTrigger>
            <DrawerContent className="px-2">
              <DrawerClose
                className={buttonVariants({
                  variant: "default",
                  size: "icon",
                  className: "absolute top-5 start-5",
                })}
              >
                <X />
              </DrawerClose>
              <DrawerTitle className="hidden" />
              <ReservationDuration
                unitName={data?.title}
                unitCode={data?.id}
                total={data?.totalPrice!}
                original={data?.originalPrice!}
                from={checkInDate}
                to={checkOutDate}
                bookingId={bookingId}
                insurance={data?.refundableAmount}
                refundableInsurance={data?.refundableInsurance}
              />
            </DrawerContent>
          </Drawer>
        </nav>
      ) : null}
      <Image
        loading="lazy"
        src={pattern}
        alt="pattern"
        className="absolute -bottom-32 end-0 max-w-md -z-[1]"
      />
    </div>
  );
};

export default memo(UnitPage);
